// 教學專欄 (D-270) outside the article pages: the newest articles on the homepage and the 相關文章 list under a
// tool page. The browser holds no article data of its own: /api/articles answers with what is public right now
// (src/articles/route.ts), so an article never shows before its publish time and a block with nothing to show is left out.
import { useEffect, useState } from "react";
import { locale, lp, t } from "./i18n";
import "./articles.css";

export type ArticleCardData = {
  slug: string; href: string; title: string; teaser: string; publishAt: string; dateLabel: string; readingLabel: string;
  cover: { src: string; width: number; height: number }; mascot: boolean;
};

/** Public articles of this page's language: the newest ones, or those that use one free tool or feature. null while loading or on failure. */
export function useArticleCards(limit: number, tool?: string, feature?: string): ArticleCardData[] | null {
  const [cards, setCards] = useState<ArticleCardData[] | null>(null);
  useEffect(() => {
    let live = true;
    const query = new URLSearchParams({ locale, limit: String(limit), ...(tool ? { tool } : {}), ...(feature ? { feature } : {}) });
    fetch(`/api/articles?${query}`).then((response) => response.ok ? response.json() : null)
      .then((body: { articles?: ArticleCardData[] } | null) => { if (live) setCards(body?.articles ?? []); }, () => { if (live) setCards([]); });
    return () => { live = false; };
  }, [limit, tool, feature]);
  return cards;
}

/** Same markup as the server's article cards (src/articles/render.ts articleCard). */
export function ArticleCardList({ cards, compact = false }: { cards: ArticleCardData[]; compact?: boolean }) {
  return <ul className={`ar-card-grid${compact ? " ar-card-grid-compact" : ""}`}>{cards.map((card) => <li className="ar-card" key={card.slug}>
    <a className={`ar-card-media${card.mascot ? " is-mascot" : ""}`} href={card.href} tabIndex={-1} aria-hidden="true"><img src={card.cover.src} width={card.cover.width} height={card.cover.height} alt="" loading="lazy" decoding="async" /></a>
    <div className="ar-card-body">
      <p className="ar-meta"><time dateTime={card.publishAt}>{card.dateLabel}</time><span>{card.readingLabel}</span></p>
      <h3><a href={card.href}>{card.title}</a></h3>
      <p>{card.teaser}</p>
    </div>
  </li>)}</ul>;
}

/** Homepage: the three newest articles. */
export function HomeArticles() {
  const cards = useArticleCards(3);
  if (!cards?.length) return null;
  return <section className="home-articles" id="articles" data-home-module="M6-ARTICLES" data-cta-position="home_articles" aria-labelledby="home-articles-title">
    <div className="home-articles-head">
      <div><p className="eyebrow">{t("教學專欄")}</p><h2 id="home-articles-title">{t("班級經營，一次解決一個小問題")}</h2></div>
      <a href={lp("/articles")}>{t("看全部文章")} <span aria-hidden="true">→</span></a>
    </div>
    <ArticleCardList cards={cards} />
  </section>;
}

/** Under a free tool: articles whose frontmatter lists this tool. */
export function ToolArticles({ tool }: { tool: string }) {
  const cards = useArticleCards(3, tool);
  if (!cards?.length) return null;
  return <section className="ft-articles" data-cta-position="tool_articles" aria-labelledby="ft-articles-title">
    <h2 id="ft-articles-title">{t("相關文章")}</h2>
    <ArticleCardList cards={cards} />
  </section>;
}

/** /features: a few articles under the feature list. */
export function FeatureArticles() {
  const cards = useArticleCards(3);
  if (!cards?.length) return null;
  return <section className="mk-section" aria-labelledby="features-articles" data-cta-position="features_articles">
    <div className="mk-section-head"><p className="eyebrow">{t("教學專欄")}</p><h2 id="features-articles">{t("班級經營的小方法")}</h2></div>
    <ArticleCardList cards={cards} />
  </section>;
}

/** /features/:slug: articles whose frontmatter lists this feature (D-278), so each feature page leads on to the articles about it. */
export function FeatureRelatedArticles({ feature }: { feature: string }) {
  const cards = useArticleCards(3, undefined, feature);
  if (!cards?.length) return null;
  return <section className="mk-section" aria-labelledby={`${feature}-articles`} data-cta-position="feature_articles">
    <div className="mk-section-head"><p className="eyebrow">{t("教學專欄")}</p><h2 id={`${feature}-articles`}>{t("相關文章")}</h2></div>
    <ArticleCardList cards={cards} />
  </section>;
}
